import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { canOperationalApprove, useAuth } from "../lib/auth";
import { fmtNum } from "../lib/format";

const CONDITIONS = ["good", "damaged", "under_repair", "missing"];

export default function AssetClosing() {
  const { user } = useAuth();
  const canApprove = canOperationalApprove(user);
  const [reg, setReg] = useState(null);
  const [roomStatus, setRoomStatus] = useState([]);
  const [units, setUnits] = useState([]);
  const [closings, setClosings] = useState([]);
  const [physical, setPhysical] = useState({}); // key -> qty
  const [remarks, setRemarks] = useState({}); // key -> remark
  const [unitState, setUnitState] = useState({}); // unit_id -> {present, condition, remark}
  const [closingDate, setClosingDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [view, setView] = useState("new");
  const [loadError, setLoadError] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      setLoadError(false);
      const [r, rs, u, c, it] = await Promise.all([
        api.get("/assets/reports/register"),
        api.get("/assets/reports/room-status"),
        api.get("/assets/units"),
        api.get("/assets/closings"),
        api.get("/assets/items"),
      ]);
      setReg(r.data);
      setRoomStatus(rs.data);
      setClosings(c.data);
      setItems(it.data);
      const liveUnits = u.data.filter((x) => x.status !== "disposed");
      setUnits(liveUnits);
      const us = {};
      liveUnits.forEach((x) => {
        us[x.id] = {
          present: true,
          condition: x.condition || "good",
          remark: "",
        };
      });
      setUnitState(us);
    } catch (e) {
      setLoadError(true);
      toast.error("Could not load asset closing data — please retry");
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Build item-count worksheet rows (bulk + consumable) with LIVE expected balances.
  // Store: every ACTIVE non-tagged item (expected may be 0 → enables overage/variance checks).
  // Rooms: any posted balance from room-status.
  const skuToId = {};
  (reg?.items || []).forEach((i) => {
    skuToId[i.sku] = { id: i.item_id, mode: i.tracking_mode };
  });
  const regStore = {};
  (reg?.items || []).forEach((i) => {
    regStore[i.item_id] = i.store || 0;
  });
  const rows = [];
  items
    .filter((i) => i.active && i.tracking_mode !== "tagged")
    .forEach((i) => {
      rows.push({
        item_id: i.id,
        name: i.name,
        sku: i.sku,
        location: "A_STORE",
        room_id: null,
        locLabel: "Store",
        expected: regStore[i.id] || 0,
      });
    });
  roomStatus.forEach((rm) =>
    (rm.lines || []).forEach((l) => {
      const map = skuToId[l.sku];
      if (map && map.mode !== "tagged" && l.qty > 0) {
        rows.push({
          item_id: map.id,
          name: l.item,
          sku: l.sku,
          location: "A_ROOM",
          room_id: rm.room_id,
          locLabel: `Room ${rm.room}`,
          expected: l.qty,
        });
      }
    }),
  );

  const keyOf = (r) => `${r.item_id}|${r.location}|${r.room_id || ""}`;
  const setQty = (k, v) => setPhysical({ ...physical, [k]: v });
  const setRemark = (k, v) => setRemarks({ ...remarks, [k]: v });
  const setUnit = (id, f, v) =>
    setUnitState((p) => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const submit = async () => {
    if (loadError || !reg)
      return toast.error("Data not loaded — please retry before submitting");
    const lines = [];
    for (const r of rows) {
      const k = keyOf(r);
      const q = physical[k] === undefined ? r.expected : Number(physical[k]);
      const variance = q - r.expected;
      if (variance !== 0 && !(remarks[k] || "").trim()) {
        return toast.error(
          `Reason required for ${r.name} @ ${r.locLabel} (variance ${variance})`,
        );
      }
      lines.push({
        kind: "item",
        item_id: r.item_id,
        location: r.location,
        room_id: r.room_id,
        physical_qty: q,
        remark: remarks[k] || null,
      });
    }
    for (const u of units) {
      const s = unitState[u.id] || {
        present: true,
        condition: u.condition,
        remark: "",
      };
      if (!s.present && !(s.remark || "").trim()) {
        return toast.error(`Reason required for missing asset ${u.asset_id}`);
      }
      lines.push({
        kind: "unit",
        unit_id: u.id,
        present: s.present,
        condition: s.condition,
        remark: s.remark || null,
      });
    }
    try {
      await api.post("/assets/closings", { closing_date: closingDate, lines });
      toast.success("Asset closing submitted");
      setPhysical({});
      setRemarks({});
      setView("list");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approve = async (c) => {
    try {
      await api.post(`/assets/closings/${c.id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const reject = async (c) => {
    try {
      await api.post(`/assets/closings/${c.id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <Button
          size="sm"
          variant={view === "new" ? "default" : "outline"}
          onClick={() => setView("new")}
          data-testid="asset-closing-new-tab"
        >
          New Count
        </Button>
        <Button
          size="sm"
          variant={view === "list" ? "default" : "outline"}
          onClick={() => setView("list")}
          data-testid="asset-closing-list-tab"
        >
          History
        </Button>
      </div>

      {view === "new" ? (
        <>
          {loadError && (
            <Card
              className="p-4 mb-3 border-red-300 bg-red-50 text-sm text-red-800 flex items-center justify-between"
              data-testid="asset-closing-load-error"
            >
              <span>Could not load asset closing data.</span>
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                data-testid="asset-closing-retry"
              >
                Retry
              </Button>
            </Card>
          )}
          {reg && !reg.opening_finalized && (
            <Card className="p-3 mb-3 border-amber-300 bg-amber-50 text-sm text-amber-900">
              Setup Pending — opening stock is draft. Counts reconcile against
              posted transactions only.
            </Card>
          )}
          <Card className="p-4 mb-3 max-w-xs">
            <label className="text-xs text-slate-500">Closing Date</label>
            <Input
              type="date"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              className="h-10 mt-1"
              data-testid="asset-closing-date"
            />
          </Card>

          <Card className="p-0 overflow-x-auto mb-4">
            <div className="p-3 text-sm font-medium text-slate-700 border-b border-slate-100">
              Item Counts — Consumables & Bulk Reusable (by location)
            </div>
            <table className="vr-table" data-testid="asset-closing-items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Location</th>
                  <th className="num">Expected</th>
                  <th className="num">Physical</th>
                  <th className="num">Variance</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const k = keyOf(r);
                  const q =
                    physical[k] === undefined
                      ? r.expected
                      : Number(physical[k]);
                  const v = q - r.expected;
                  return (
                    <tr key={k}>
                      <td>
                        {r.name}
                        <div className="text-[10px] text-slate-400 font-mono">
                          {r.sku}
                        </div>
                      </td>
                      <td className="text-xs text-slate-600">{r.locLabel}</td>
                      <td className="num">{fmtNum(r.expected)}</td>
                      <td className="num">
                        <Input
                          type="number"
                          min="0"
                          value={physical[k] ?? r.expected}
                          onChange={(e) => setQty(k, e.target.value)}
                          className="h-9 w-24 ml-auto text-right"
                          data-testid={`asset-phys-${r.sku}-${r.room_id || "store"}`}
                        />
                      </td>
                      <td
                        className={`num ${v !== 0 ? (v < 0 ? "text-red-700" : "text-emerald-700") : ""}`}
                      >
                        {v > 0 ? `+${v}` : v}
                      </td>
                      <td>
                        <Input
                          value={remarks[k] || ""}
                          onChange={(e) => setRemark(k, e.target.value)}
                          placeholder={v !== 0 ? "required" : "optional"}
                          className="h-9"
                        />
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-slate-500 py-5">
                      No posted item balances to count yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

          {units.length > 0 && (
            <Card className="p-0 overflow-x-auto mb-4">
              <div className="p-3 text-sm font-medium text-slate-700 border-b border-slate-100">
                Tagged Asset Verification (by permanent Asset ID)
              </div>
              <table className="vr-table" data-testid="asset-closing-units">
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Item</th>
                    <th>Location</th>
                    <th>Present?</th>
                    <th>Condition</th>
                    <th>Reason (if not found)</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => {
                    const s = unitState[u.id] || {
                      present: true,
                      condition: u.condition,
                      remark: "",
                    };
                    return (
                      <tr key={u.id}>
                        <td className="font-mono text-xs">{u.asset_id}</td>
                        <td>{u.item_name}</td>
                        <td className="text-xs text-slate-600">
                          {u.location}
                          {u.room ? ` · Room ${u.room}` : ""}
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={s.present}
                            onChange={(e) =>
                              setUnit(u.id, "present", e.target.checked)
                            }
                            data-testid={`asset-unit-present-${u.asset_id}`}
                          />
                        </td>
                        <td>
                          <Select
                            value={s.condition}
                            onValueChange={(v) => setUnit(u.id, "condition", v)}
                          >
                            <SelectTrigger className="h-9 w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CONDITIONS.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {c.replace("_", " ")}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td>
                          <Input
                            value={s.remark}
                            onChange={(e) =>
                              setUnit(u.id, "remark", e.target.value)
                            }
                            placeholder={!s.present ? "required" : "optional"}
                            className="h-9"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          <Button
            className="h-11 bg-slate-900 text-white hover:bg-slate-800 px-8"
            onClick={submit}
            data-testid="asset-closing-submit"
          >
            Submit Asset Closing
          </Button>
          <div className="text-xs text-slate-400 mt-2">
            Submitted counts are immutable. Non-zero variances need a reason and
            Supervisor approval. Approval records the variance for audit; stock
            corrections use audited adjustments.
          </div>
        </>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="vr-table" data-testid="asset-closings-history">
            <thead>
              <tr>
                <th>Date</th>
                <th className="num">Physical</th>
                <th className="num">Expected</th>
                <th className="num">Variance</th>
                <th>Status</th>
                <th>By</th>
                <th>Approved by</th>
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {closings.map((c) => (
                <tr key={c.id}>
                  <td>{c.closing_date}</td>
                  <td className="num">{fmtNum(c.physical_total)}</td>
                  <td className="num">{fmtNum(c.expected_total)}</td>
                  <td
                    className={`num font-semibold ${c.variance_total < 0 ? "text-red-700" : c.variance_total > 0 ? "text-emerald-700" : ""}`}
                  >
                    {c.variance_total > 0
                      ? `+${c.variance_total}`
                      : c.variance_total}
                  </td>
                  <td>
                    <Badge
                      className={
                        c.status === "approved"
                          ? "bg-emerald-100 border text-emerald-800"
                          : c.status === "rejected"
                            ? "bg-red-100 border text-red-800"
                            : "bg-amber-100 border text-amber-800"
                      }
                    >
                      {c.status}
                    </Badge>
                  </td>
                  <td className="text-xs">{c.submitted_by_name}</td>
                  <td className="text-xs">{c.approved_by_name || "—"}</td>
                  <td className="no-print">
                    {canApprove && c.status === "submitted" && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => approve(c)}
                          data-testid={`asset-approve-${c.id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reject(c)}
                          data-testid={`asset-reject-${c.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {closings.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-500 py-5">
                    No asset closings yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
